/**
 * Horario de trabajo del despachador (2026-09-30). Mismo formato que el backend (rider-schedule.util.ts), la app y Supabase:
 * tramos { dia 1..7 ISO (1 = lunes), desde "HH:MM", hasta "HH:MM" } en hora de Lima. Un tramo con hasta < desde cruza la
 * medianoche y cuenta para el día en que empieza. Un día sin tramos es día libre. El GPS de la app solo va prendido dentro
 * del horario o mientras el despachador tenga un punto en curso.
 */
export interface RiderScheduleSlot {
  dia: number
  desde: string
  hasta: string
}

export const SCHEDULE_DAYS: { dia: number; label: string; short: string }[] = [
  { dia: 1, label: "Lunes", short: "Lun" },
  { dia: 2, label: "Martes", short: "Mar" },
  { dia: 3, label: "Miércoles", short: "Mié" },
  { dia: 4, label: "Jueves", short: "Jue" },
  { dia: 5, label: "Viernes", short: "Vie" },
  { dia: 6, label: "Sábado", short: "Sáb" },
  { dia: 7, label: "Domingo", short: "Dom" }
]

export const DEFAULT_SLOT = { desde: "07:00", hasta: "22:00" } as const

/** Horario por defecto de todos los despachadores: lunes a sábado de 07:00 a 22:00. */
export const DEFAULT_RIDER_SCHEDULE: RiderScheduleSlot[] = [1, 2, 3, 4, 5, 6].map((dia) => ({ dia, ...DEFAULT_SLOT }))

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/
const DAY_MINUTES = 24 * 60
const WEEK_MINUTES = 7 * DAY_MINUTES

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number)
  return h * 60 + m
}

const toWeekInterval = (slot: RiderScheduleSlot): { start: number; end: number } => {
  const desde = toMinutes(slot.desde)
  const hasta = toMinutes(slot.hasta)
  const start = (slot.dia - 1) * DAY_MINUTES + desde
  return { start, end: start + (hasta > desde ? hasta - desde : hasta + DAY_MINUTES - desde) }
}

/** Primer error del horario, o null si es válido (misma regla que el backend). */
export function validateRiderSchedule(slots: readonly RiderScheduleSlot[]): string | null {
  for (const slot of slots) {
    if (!TIME_REGEX.test(slot.desde) || !TIME_REGEX.test(slot.hasta)) return "Completa la hora de inicio y de fin de todos los tramos"
    if (slot.desde === slot.hasta) return "La hora de inicio y la de fin de un tramo no pueden ser iguales"
  }
  const intervals = slots.map(toWeekInterval)
  for (let i = 0; i < intervals.length; i++) {
    for (let j = i + 1; j < intervals.length; j++) {
      const a = intervals[i]
      const b = intervals[j]
      if ([-WEEK_MINUTES, 0, WEEK_MINUTES].some((shift) => a.start < b.end + shift && b.start + shift < a.end)) return "Hay tramos del horario que se solapan"
    }
  }
  return null
}

export function sortSchedule(slots: readonly RiderScheduleSlot[]): RiderScheduleSlot[] {
  return slots.map(({ dia, desde, hasta }) => ({ dia, desde, hasta })).sort((a, b) => a.dia - b.dia || a.desde.localeCompare(b.desde))
}

export function isSameSchedule(a: readonly RiderScheduleSlot[], b: readonly RiderScheduleSlot[]): boolean {
  return JSON.stringify(sortSchedule(a)) === JSON.stringify(sortSchedule(b))
}

/** Horario efectivo: el guardado, o el de por defecto si el despachador no tiene uno propio (null/ausente). */
export const effectiveSchedule = (schedule?: RiderScheduleSlot[] | null): RiderScheduleSlot[] => schedule ?? DEFAULT_RIDER_SCHEDULE

/** Resumen corto para la tabla. */
export function describeSchedule(schedule?: RiderScheduleSlot[] | null): string {
  const slots = effectiveSchedule(schedule)
  if (slots.length === 0) return "No trabaja"
  if (isSameSchedule(slots, DEFAULT_RIDER_SCHEDULE)) return "Lun a Sáb 07:00–22:00"

  const days = SCHEDULE_DAYS.filter((d) => slots.some((s) => s.dia === d.dia))
  const hours = new Set(slots.map((s) => `${s.desde}–${s.hasta}`))
  if (hours.size === 1 && slots.length === days.length) return `${days.map((d) => d.short).join(", ")} ${[...hours][0]}`
  return `Personalizado · ${days.length} ${days.length === 1 ? "día" : "días"}, ${slots.length} ${slots.length === 1 ? "tramo" : "tramos"}`
}
